import React from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { CalendarClock, Car, ListChecks, MapPin, Pencil, Trash2 } from 'lucide-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';

import EventTaskChips from './EventTaskChips';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const tactileButton = (colorKey) => (theme) => {
  const color = theme.palette[colorKey]?.main || theme.palette.primary.main;
  return {
    m: 0,
    color,
    borderColor: color,
    boxShadow: `0 4px 0 ${darken(color, 0.58)}`,
    '&:hover': {
      borderColor: color,
      bgcolor: alpha(color, 0.12),
      transform: 'translate(-1px, -1px)',
      boxShadow: `1px 5px 0 ${darken(color, 0.58)}`,
    },
    '&:active': {
      transform: 'translateY(3px)',
      boxShadow: `0 1px 0 ${darken(color, 0.58)}`,
    },
  };
};

const eventDate = (event) => {
  const start = dayjs(event.starts_at).tz(event.timezone);
  const end = dayjs(event.ends_at).tz(event.timezone);
  return `${start.format('ddd, MMM D · h:mm A')} – ${end.format('h:mm A')}`;
};

export default function EventCard({
  event,
  taskLinks = [],
  onOpen,
  onEdit,
  onDelete,
  onManageTasks,
  onAddTravel,
  deleting = false,
}) {
  const location = event.location?.name || event.location?.address;
  const past = dayjs(event.ends_at).isBefore(dayjs());
  const cancelled = event.status === 'cancelled';
  const canEdit = event.permissions?.can_edit ?? true;
  const canDelete = event.permissions?.can_delete ?? true;
  const imageUrl = event.image?.url;

  return (
    <Box
      component="article"
      role="button"
      tabIndex={0}
      aria-label={`Open ${event.title}`}
      onClick={() => onOpen?.(event)}
      onKeyDown={(keyEvent) => {
        if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
          keyEvent.preventDefault();
          onOpen?.(event);
        }
      }}
      sx={(theme) => ({
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 2, sm: 2.5 },
        border: `3px solid ${past ? theme.palette.secondary.main : theme.palette.primary.main}`,
        borderRadius: 5,
        background:
          `linear-gradient(140deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ` +
          `${alpha(theme.palette.primary.main, 0.07)} 70%, ` +
          `${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
        boxShadow:
          `0 8px 0 ${alpha(past ? theme.palette.secondary.main : theme.palette.primary.main, 0.2)}`,
        transition: 'transform 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translate(-2px, -2px)',
          boxShadow:
            `2px 10px 0 ${alpha(past ? theme.palette.secondary.main : theme.palette.primary.main, 0.28)}`,
        },
        '&:hover .event-card-image': { transform: 'scale(1.06)' },
      })}
    >
      {imageUrl && (
        <Box
          data-testid="event-card-image-backdrop"
          sx={{
            position: 'absolute',
            inset: '0 auto 0 0',
            width: { xs: '58%', sm: 300 },
            overflow: 'hidden',
            WebkitMaskImage: 'linear-gradient(to right, #000 52%, transparent 100%)',
            maskImage: 'linear-gradient(to right, #000 52%, transparent 100%)',
          }}
        >
          <Box
            className="event-card-image"
            component="img"
            src={imageUrl}
            alt=""
            sx={{
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: 'cover',
              objectPosition: 'left center',
              opacity: 0.72,
              transition: 'transform 220ms ease',
            }}
          />
        </Box>
      )}
      <Stack
        spacing={2}
        sx={{
          position: 'relative',
          zIndex: 1,
          ml: imageUrl ? { xs: '25%', sm: '185px' } : 0,
          minWidth: 0,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Typography variant="h6" sx={{ color: 'primary.main', overflowWrap: 'anywhere' }}>
            {event.title}
          </Typography>
          {(cancelled || past) && (
            <Chip
              label={cancelled ? 'Cancelled' : 'Past'}
              size="small"
              sx={{ color: 'background.default', bgcolor: 'secondary.main' }}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ color: 'primary.main' }}>
          <CalendarClock size={19} style={{ flex: '0 0 auto' }} />
          <Box>
            <Typography variant="body2" sx={{ color: 'primary.main', textAlign: 'left' }}>
              {eventDate(event)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'secondary.main' }}>
              {event.timezone}
            </Typography>
          </Box>
        </Stack>

        {location && (
          <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ color: 'secondary.main' }}>
            <MapPin size={19} style={{ flex: '0 0 auto' }} />
            <Typography variant="body2" sx={{ color: 'secondary.main', textAlign: 'left' }}>
              {event.location.name || event.location.address}
              {event.location.name && event.location.address ? ` · ${event.location.address}` : ''}
            </Typography>
          </Stack>
        )}

        {event.description && (
          <Typography
            variant="body2"
            sx={{ color: 'primary.main', textAlign: 'left', whiteSpace: 'pre-wrap' }}
          >
            {event.description}
          </Typography>
        )}

        <EventTaskChips links={taskLinks} />

        <Stack direction="row" justifyContent="flex-end" spacing={1.5} flexWrap="wrap">
          {!cancelled && !past && (
            <Button
              variant="outlined"
              startIcon={<Car size={16} />}
              onClick={(clickEvent) => { clickEvent.stopPropagation(); onAddTravel?.(event); }}
              sx={tactileButton('orange')}
            >
              Add travel
            </Button>
          )}
          {!cancelled && (
              <Button
                variant="outlined"
                startIcon={<ListChecks size={16} />}
                onClick={(clickEvent) => { clickEvent.stopPropagation(); onManageTasks?.(event); }}
                sx={tactileButton('orange')}
              >
                Tasks
              </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Pencil size={16} />}
            onClick={(clickEvent) => { clickEvent.stopPropagation(); onEdit(event); }}
            sx={tactileButton('primary')}
          >
            {canEdit ? 'Edit' : 'View'}
          </Button>
          {canDelete && (
            <Button
              variant="outlined"
              startIcon={<Trash2 size={16} />}
              onClick={(clickEvent) => { clickEvent.stopPropagation(); onDelete(event); }}
              disabled={deleting}
              sx={tactileButton('secondary')}
            >
              {deleting ? 'Cancelling...' : 'Cancel event'}
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
